import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Filter } from "lucide-react";

const RestaurantFilters = ({ search, onSearchChange, filters, setFilters, filterOptions, showFilters, onToggleFilters }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 mb-8">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={t('restaurants.searchPlaceholder')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-12 h-12 bg-card border-border focus:border-primary"
            data-testid="restaurant-search-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={onToggleFilters}
          className={`h-12 border-border ${showFilters ? 'border-primary text-primary' : ''}`}
          data-testid="filter-toggle"
        >
          <Filter className="w-4 h-4 mr-2" />
          {t('restaurants.filters')}
        </Button>
      </div>

      {showFilters && (
        <Card className="bg-card border-border p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.country')}</Label>
              <Select value={filters.country} onValueChange={(v) => setFilters({ ...filters, country: v === "all" ? "" : v })}>
                <SelectTrigger className="bg-transparent border-border" data-testid="filter-country">
                  <SelectValue placeholder={t('restaurants.allCountries')} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">{t('restaurants.allCountries')}</SelectItem>
                  {filterOptions.countries.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.michelinStars')}</Label>
              <Select value={filters.stars} onValueChange={(v) => setFilters({ ...filters, stars: v === "all" ? "" : v })}>
                <SelectTrigger className="bg-transparent border-border" data-testid="filter-stars">
                  <SelectValue placeholder={t('restaurants.allStars')} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">{t('restaurants.allStars')}</SelectItem>
                  <SelectItem value="1">⭐ 1 Star</SelectItem>
                  <SelectItem value="2">⭐⭐ 2 Stars</SelectItem>
                  <SelectItem value="3">⭐⭐⭐ 3 Stars</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.cuisineType')}</Label>
              <Select value={filters.cuisine_type} onValueChange={(v) => setFilters({ ...filters, cuisine_type: v === "all" ? "" : v })}>
                <SelectTrigger className="bg-transparent border-border" data-testid="filter-cuisine">
                  <SelectValue placeholder={t('restaurants.allCuisines')} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-[200px]">
                  <SelectItem value="all">{t('restaurants.allCuisines')}</SelectItem>
                  {filterOptions.cuisines.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Price Range</Label>
              <Select value={filters.price_range || ""} onValueChange={(v) => setFilters({ ...filters, price_range: v === "all" ? "" : v })}>
                <SelectTrigger className="bg-transparent border-border" data-testid="filter-price">
                  <SelectValue placeholder="All Prices" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">All Prices</SelectItem>
                  <SelectItem value="1">€ Budget</SelectItem>
                  <SelectItem value="2">€€ Moderate</SelectItem>
                  <SelectItem value="3">€€€ Expensive</SelectItem>
                  <SelectItem value="4">€€€€ Luxury</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {(filters.country || filters.stars || filters.cuisine_type || filters.price_range) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ country: "", stars: "", cuisine_type: "", price_range: "" })}
              className="mt-4 text-muted-foreground"
              data-testid="clear-filters"
            >
              {t('restaurants.clearFilters')}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
};

export default RestaurantFilters;
